import { Layers, KeyRound, ListChecks } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { ConsultButton } from "@/components/CtaButtons";
import { useLanguage } from "@/i18n/LanguageContext";
import { MATERIAL_CATEGORIES, MATERIAL_TIERS } from "@/data/content";

const TIER_DESC = {
  Basic: "Functional, budget-conscious specifications.",
  Standard: "Balanced quality and value — the most chosen.",
  Premium: "High-end finishes and fixtures.",
  Custom: "Your exact choices, planned together.",
};

export const MaterialsSection = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-offwhite py-24 md:py-32" data-testid="materials-section">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 md:px-8 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal>
            <SectionHeading eyebrow={t("materials.eyebrow")} title={t("materials.title")} sub={t("materials.sub")} />
            <p className="mt-6 text-xs leading-relaxed text-charcoal/50">{t("materials.brandsNote")}</p>
            <ConsultButton testid="materials-consult-button" className="mt-8">{t("cta.talkTeam")}</ConsultButton>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <div className="grid gap-5 sm:grid-cols-2">
            {MATERIAL_CATEGORIES.map((cat, i) => (
              <Reveal key={cat.id} delay={i * 0.06}>
                <div data-testid={`material-cat-${cat.id}`} className="h-full rounded-2xl border border-charcoal/10 bg-white p-7">
                  <Layers className="h-5 w-5 text-gold-dark" />
                  <h3 className="mt-4 font-display text-xl text-charcoal">{t(`materials.${cat.id}`)}</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-charcoal/65">
                    {cat.items.map((item) => <li key={item}>· {item}</li>)}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
              {MATERIAL_TIERS.map((tier) => (
                <div key={tier} data-testid={`material-tier-${tier.toLowerCase()}`}
                  className={`rounded-2xl p-5 ${tier === "Custom" ? "bg-charcoal text-offwhite" : "border border-charcoal/10 bg-white"}`}>
                  <p className={`font-display text-lg ${tier === "Custom" ? "text-gold" : "text-charcoal"}`}>{tier}</p>
                  <p className={`mt-1.5 text-xs leading-relaxed ${tier === "Custom" ? "text-white/60" : "text-charcoal/55"}`}>{TIER_DESC[tier]}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const TurnkeySection = () => {
  const { t } = useLanguage();
  const individual = ["Design", "Structural planning", "Renovation", "Interior", "Electrical", "Plumbing", "Flooring", "Painting", "Landscaping"];
  return (
    <section className="bg-charcoal py-24 md:py-32" data-testid="turnkey-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal><SectionHeading dark center eyebrow={t("turnkey.eyebrow")} title={t("turnkey.title")} /></Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <Reveal>
            <div data-testid="individual-services-card" className="h-full rounded-3xl border border-white/12 bg-white/[0.04] p-10">
              <ListChecks className="h-7 w-7 text-gold" />
              <p className="mt-6 font-display text-6xl text-white/15">01</p>
              <h3 className="mt-2 font-display text-3xl text-offwhite">{t("turnkey.individual")}</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/65">{t("turnkey.individualDesc")}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {individual.map((s) => (
                  <span key={s} className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/70">{s}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div data-testid="turnkey-card" className="relative h-full overflow-hidden rounded-3xl border border-gold/40 bg-gold/[0.08] p-10">
              <KeyRound className="h-7 w-7 text-gold" />
              <p className="mt-6 font-display text-6xl text-gold/25">02</p>
              <h3 className="mt-2 font-display text-3xl text-gold">{t("turnkey.full")}</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/70">{t("turnkey.fullDesc")}</p>
              <p className="mt-6 font-display text-lg italic text-white/80">From idea to handover.</p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-12 text-center">
          <ConsultButton testid="turnkey-discuss-button">{t("cta.discuss")}</ConsultButton>
        </Reveal>
      </div>
    </section>
  );
};

const FOUNDERS = [
  { name: "Deepak Kumar P G", photo: null },
  { name: "Naveen Kumar P N", photo: "/assets/founder-naveen.jpeg" },
];

export const FoundersSection = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-white py-24 md:py-32" data-testid="founders-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal><SectionHeading center eyebrow={t("founders.eyebrow")} title={t("founders.title")} sub={t("founders.body")} /></Reveal>
        <div className="mx-auto mt-14 grid max-w-3xl gap-6 sm:grid-cols-2">
          {FOUNDERS.map((founder, i) => (
            <Reveal key={founder.name} delay={i * 0.1}>
              <div data-testid={`founder-card-${i}`} className="rounded-3xl border border-charcoal/10 bg-offwhite p-10 text-center transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-gold-dark/40">
                {founder.photo ? (
                  <img
                    src={founder.photo}
                    alt={`${founder.name} — Founder, D & N Constructions`}
                    loading="lazy"
                    data-testid={`founder-photo-${i}`}
                    className="mx-auto h-36 w-36 rounded-full object-cover object-[center_18%] shadow-lg ring-2 ring-gold/60"
                  />
                ) : (
                  <div className="mx-auto flex h-36 w-36 items-center justify-center rounded-full bg-charcoal font-display text-4xl text-gold ring-2 ring-gold/30">
                    {founder.name.charAt(0)}
                  </div>
                )}
                <h3 className="mt-6 font-display text-2xl text-charcoal">{founder.name}</h3>
                <p className="mt-1.5 text-xs font-bold uppercase tracking-[0.2em] text-gold-dark">Co-Founder</p>
                <p className="mt-4 text-sm leading-relaxed text-charcoal/60">
                  10+ years of construction experience across Tumakuru and Bengaluru — personally involved in every project.
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
